import moment from 'moment';

/**
 * 訂房曲線「正常帶」用了哪幾天當對照樣本，其中有幾天是國定假日。
 *
 * 正常帶的歷史樣本是把桶起始日往回推 364×k 天 (k = 1..n)，見後端
 * ReconstructsBookingPace::computePaceBand()。364 天 = 52 週，對齊星期是刻意的 ——
 * 休閒飯店週末與平日的落差極大，用同曆日回推會拿週六去比平日。
 *
 * 但農曆節慶不跟著星期走，所以它會在樣本之間漂移。實測 2026/09/14 (一) 的五個對照日裡，
 * 2021/09/20 是中秋連假的調整放假、2024/09/16 是中秋 (9/17) 前的夾日，兩天都衝到 58 間，
 * 把中位數整個往上抬 —— 於是一個普通的九月平日被判成「落後正常帶 24.5pt」。
 *
 * 對齊方式本身不該改 (改了星期會錯位，那更糟)，要做的是把這個代價講出來：
 * 讓使用者知道這一天的正常帶裡混了幾天假日。
 */

/** 桶起始日的對照樣本日期 (往回 364×k 天)，回傳 YYYY-MM-DD 陣列。 */
export function bandSampleDates(dat, years) {
  const n = Number(years) || 0;
  if (!dat || n < 1) return [];
  const base = moment(dat, ['YYYY/MM/DD', 'YYYY-MM-DD']);
  if (!base.isValid()) return [];
  const out = [];
  for (let k = 1; k <= n; k += 1) {
    out.push(base.clone().subtract(364 * k, 'days').format('YYYY-MM-DD'));
  }
  return out;
}

const shiftIso = (iso, n) => moment(iso, 'YYYY-MM-DD').add(n, 'days').format('YYYY-MM-DD');

/**
 * 樣本裡被假日墊高的那幾天，回傳 `[{ date, kind }]`，kind 為 'holiday' 或 'bridge'。
 *
 * 光看「是不是國定假日」會漏掉一半 —— 2026/09/14 的五個對照日裡，2021/09/20 本身是調整放假
 * (holiday)，但 2024/09/16 只是中秋 (9/17) 前一天的夾日，它自己不在假日表裡，實際住房卻同樣
 * 衝到 58 間。只認 holiday 會說「5 天裡 1 天是假日」，把失真講成一半。
 *
 * 夾日的判準是「前一天或後一天是國定假日」。週末不算 —— fetchSpecialDates 只回
 * category === 'special'，週六日本來就不在裡面，否則每個週一週五都會被判成夾日。
 *
 * `specialDates` 是 holidayCalendar.fetchSpecialDates() 的 Set；還沒載入時回空陣列 ——
 * 「不知道」不能顯示成任何結論。
 */
export function holidaySamples(dat, years, specialDates) {
  if (!specialDates || !specialDates.size) return [];
  return bandSampleDates(dat, years)
    .map((d) => {
      if (specialDates.has(d)) return { date: d, kind: 'holiday' };
      if (specialDates.has(shiftIso(d, -1)) || specialDates.has(shiftIso(d, 1))) {
        return { date: d, kind: 'bridge' };
      }
      return null;
    })
    .filter(Boolean);
}

/** 把 holidaySamples 的結果寫成一句人看得懂的話。 */
export function holidaySampleText(samples, years) {
  if (!samples || !samples.length) return '';
  const kinds = samples.some((x) => x.kind === 'bridge')
    ? (samples.every((x) => x.kind === 'bridge') ? '連假夾日' : '國定假日或連假夾日')
    : '國定假日';
  return `${years} 個對照樣本中 ${samples.length} 天是${kinds} (${samples.map((x) => x.date).join('、')})，正常帶偏高`;
}

/**
 * 樣本涵蓋的日期範圍，供一次把需要的年度假日抓齊。
 * 前後各多一天：夾日要看樣本的鄰日是不是假日，邊界那兩天不多抓就判不出來。
 */
export function sampleRange(fromDat, toDat, years) {
  const n = Number(years) || 0;
  if (!fromDat || !toDat || n < 1) return null;
  const from = moment(fromDat, ['YYYY/MM/DD', 'YYYY-MM-DD']).subtract(364 * n + 1, 'days');
  const to = moment(toDat, ['YYYY/MM/DD', 'YYYY-MM-DD']).subtract(364 - 1, 'days');
  if (!from.isValid() || !to.isValid()) return null;
  return { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') };
}
